import * as z from 'zod';

import { CreateGroupHookupValidationSchema, UpdateGroupHookupValidationSchema } from '@form';

import { UserDataType, VideoSdkRoomType } from '.';

export enum GroupHookupStatusType {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  'NOT_ACCEPTED' = 'NOT ACCEPTED'
}

export enum GroupHookupDefaultCategoryType {
  'BIG_EVENT' = 'BIG_EVENT'
}

export type CreateGroupHookupReqBodyType = z.infer<typeof CreateGroupHookupValidationSchema>;
export type UpdateGroupHookupReqBodyType = z.infer<typeof UpdateGroupHookupValidationSchema>;

export type RejectGroupHookupParamsType = {
  uuid: string;
  reason: string;
};

export type CancelGroupHookupParamsType = {
  uuid: string;
  reason: string;
};

export type CreateGroupHookupResType = {
  title: string;
  category: string;
  startDate: string;
  duration: number;
  description?: string;
};

export type GetGroupHookupQueryParamsType = {
  uuid: string;
};

export type GroupHookupCategoryDetailsType = {
  id: string;
  uuid: string;
  createdOn: string;
  updatedOn: string;
  code: string;
  color: string;
  name: string;
  defaultDuration: number;
  defaultParticipantsLimit: number;
  enabled: boolean;
  icon: string | null;
  organizationUuid: string;
};

export interface GroupHookupItemListType {
  uuid: string;
  hostFullName: string;
  title: string;
  category: string;
  startDate: string | number[]; // ISO date-time string or [year, month, day, hour, minute] array
  eventTimezone?: string; // IANA timezone ID (e.g., "Europe/London", "America/New_York")
  hostTimezone?: string; // Host's timezone at event creation time (for reference)
  status: keyof typeof GroupHookupStatusType;
  duration: number;
  slotsLeft: number;
  hostImageUrl: string;
  description: string | null;
  highlighted: boolean;
  participants: string[];
  sponsored: boolean;
  hostRoles?: string[];
  paidEvent?: boolean;
  eventPrice?: number;
  priceId?: string;
  // Recurring fields
  seriesId?: string;
  isPartOfSeries?: boolean;
  occurrenceNumber?: number;
}

export type GroupHookupParticipantType = Pick<
  UserDataType,
  | 'id'
  | 'uuid'
  | 'username'
  | 'firstName'
  | 'linkedInUrl'
  | 'imageUrl'
  | 'fullName'
  | 'jobPosition'
  | 'city'
  | 'companyName'
  | 'linkedWithGoogleCalendar'
  | 'location'
> & {
  videoCallExtended: boolean;
};

interface ZoomMeetingConfig {
  password: string;
  meetingId: string;
  joinUrl: string;
  meetingUuid: string;
}

interface ZoomVideoSdkConfig {
  sessionName: string;
  sdkKey: string;
}

export interface ZoomRoomResponse {
  webhook: null;
  autoCloseConfig: null;
  disabled: boolean;
  createdAt: null;
  updatedAt: null;
  roomId: string;
  customRoomId: null;
  links: null;
  id: null;
  config: ZoomMeetingConfig;
  meetingLink: string;
  customMeetingId: null;
}

export interface ZoomVideoSdkRoomResponse {
  webhook: null;
  autoCloseConfig: null;
  disabled: boolean;
  createdAt: null;
  updatedAt: null;
  roomId: string;
  customRoomId: null;
  links: null;
  id: null;
  config: ZoomVideoSdkConfig;
  meetingLink: string;
  customMeetingId: null;
}

export type RoomResponseType = VideoSdkRoomType | ZoomRoomResponse | ZoomVideoSdkRoomResponse;

export interface GroupHookupDetailsType {
  id: string;
  uuid: string;
  title: string;
  host: string;
  category: string;
  participants: GroupHookupParticipantType[];
  videoCallId: string;
  startDate: string | number[]; // ISO date-time string or [year, month, day, hour, minute] array
  eventTimezone?: string; // IANA timezone ID (e.g., "Europe/London", "America/New_York")
  hostTimezone?: string; // Host's timezone at event creation time (for reference)
  duration: number;
  nonApprovedReason: null | string; // replace string with the actual type if known
  status: keyof typeof GroupHookupStatusType;
  description: string;
  whoShouldJoin: string;
  whyShouldJoin: string;
  whatWillDiscuss: string;
  roomResponse: RoomResponseType;
  token: string | null;
  slotsLeft: number;
  canUserJoin: boolean;
  shouldForceExit: boolean;
  hostParticipant: GroupHookupParticipantType;
  participantsUUIDs: string[];
  userNeedsToAttendFirst: (authenticatedUserUUID: string) => boolean;
  isHost: (authenticatedUserUUID: string) => boolean;
  imageUrl?: string;
  sponsored: boolean;
  paidEvent?: boolean;
  eventPrice?: number;
  priceId?: string;
  // Recurring fields
  seriesId?: string;
  isPartOfSeries?: boolean;
  occurrenceNumber?: number;
  recurrenceMetadata?: RecurrenceMetadataType;
}

export interface GroupHookupConnectionItemListType {
  userUuid: string;
  id: string;
  fullName: string;
  username: string;
  jobPosition: string;
  companyName: string;
  imageUrl: string;
  linkedinUrl?: string;
  groupHookupName: string | null;
  groupHookupDate: string;
  startDateTime: string;
  timeCycleName: string;
  sorting_parameter?: string;
  sorting_direction?: string;
}

export type GroupHookupConnectionsQueryParamsType = {
  fullName?: string;
  groupHookupName?: string;
  fromDate?: string | null;
  toDate?: string | null;
  sorting_parameter?: string;
  sorting_direction?: string;
};

// Recurring GroupHookup types
export type RecurrenceFrequency = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'BIMONTHLY' | 'QUARTERLY';

export type RecurrenceEndCondition = 'END_DATE' | 'AFTER_N_OCCURRENCES';

export type DayOfWeek = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export interface RecurrencePatternType {
  frequency: RecurrenceFrequency;
  interval: number; // e.g., 1 for every week, 2 for every 2 weeks
  daysOfWeek?: DayOfWeek[]; // Required for WEEKLY frequency
  endCondition: RecurrenceEndCondition;
  endDate?: string; // ISO datetime string, required if endCondition is END_DATE
  maxOccurrences?: number; // Required if endCondition is AFTER_N_OCCURRENCES (max 180)
}

export interface RecurrenceMetadataType {
  frequency: string;
  interval: number;
  daysOfWeek?: string[];
  originalStartDate: string;
  seriesEndDate: string;
  totalOccurrences: number;
  modifiedOccurrences: string[]; // UUIDs of individually modified occurrences
}

export interface CreateRecurringGroupHookupReqBodyType {
  template: CreateGroupHookupReqBodyType;
  recurrencePattern: RecurrencePatternType;
}
